

import { Direction } from '@angular/cdk/bidi';
import { InjectionToken, TemplateRef, Type } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';

import { ThemeType } from '@ant-design/icons-angular';

import { MwcBreakpointEnum } from 'mwc/core/services';
import {
  MwcSafeAny,
  MwcShapeSCType,
  MwcSizeDSType,
  MwcSizeLDSType,
  MwcSizeMDSType,
  MwcTSType
} from 'mwc/core/types';

interface MonacoEnvironment {
  globalAPI?: boolean;
  baseUrl?: string;
  getWorker?(workerId: string, label: string): Promise<Worker> | Worker;
  getWorkerUrl?(workerId: string, label: string): string;
}

export interface MwcConfig {
  affix?: AffixConfig;
  select?: SelectConfig;
  alert?: AlertConfig;
  anchor?: AnchorConfig;
  avatar?: AvatarConfig;
  backTop?: BackTopConfig;
  badge?: BadgeConfig;
  button?: ButtonConfig;
  card?: CardConfig;
  carousel?: CarouselConfig;
  cascader?: CascaderConfig;
  codeEditor?: CodeEditorConfig;
  collapse?: CollapseConfig;
  collapsePanel?: CollapsePanelConfig;
  datePicker?: DatePickerConfig;
  descriptions?: DescriptionsConfig;
  drawer?: DrawerConfig;
  dropDown?: DropDownConfig;
  empty?: EmptyConfig;
  filterTrigger?: FilterTriggerConfig;
  form?: FormConfig;
  icon?: IconConfig;
  message?: MessageConfig;
  modal?: ModalConfig;
  notification?: NotificationConfig;
  pageHeader?: PageHeaderConfig;
  pagination?: PaginationConfig;
  progress?: ProgressConfig;
  rate?: RateConfig;
  segmented?: SegmentedConfig;
  space?: SpaceConfig;
  spin?: SpinConfig;
  switch?: SwitchConfig;
  table?: TableConfig;
  tabs?: TabsConfig;
  timePicker?: TimePickerConfig;
  tree?: TreeConfig;
  treeSelect?: TreeSelectConfig;
  typography?: TypographyConfig;
  image?: ImageConfig;
  popconfirm?: PopConfirmConfig;
  popover?: PopoverConfig;
  imageExperimental?: ImageExperimentalConfig;
  theme?: Theme;
  prefixCls?: PrefixCls;
}

export interface PrefixCls {
  prefixCls?: string;
  iconPrefixCls?: string;
}

export interface Theme {
  primaryColor?: string;
  infoColor?: string;
  successColor?: string;
  processingColor?: string;
  errorColor?: string;
  warningColor?: string;
  [key: string]: string | undefined;
}

export interface SelectConfig {
  mwcBorderless?: boolean;
  mwcSuffixIcon?: TemplateRef<MwcSafeAny> | string | null;
  mwcBackdrop?: boolean;
}

export interface AffixConfig {
  mwcOffsetBottom?: number;
  mwcOffsetTop?: number;
}

export interface AlertConfig {
  mwcCloseable?: boolean;
  mwcShowIcon?: boolean;
}

export interface AvatarConfig {
  mwcShape?: MwcShapeSCType;
  mwcSize?: MwcSizeLDSType | number;
  mwcGap?: number;
}

export interface AnchorConfig {
  mwcBounds?: number;
  mwcOffsetBottom?: number;
  mwcOffsetTop?: number;
  mwcShowInkInFixed?: boolean;
}

export interface BackTopConfig {
  mwcVisibilityHeight?: number;
}

export interface BadgeConfig {
  mwcColor?: number;
  mwcOverflowCount?: number;
  mwcShowZero?: number;
}

export interface ButtonConfig {
  mwcSize?: 'large' | 'default' | 'small';
}

export interface CodeEditorConfig {
  assetsRoot?: string | SafeUrl;
  extraConfig?: MwcSafeAny;
  defaultEditorOption?: MwcSafeAny;
  useStaticLoading?: boolean;
  monacoEnvironment?: MonacoEnvironment;

  onLoad?(): void;

  onFirstEditorInit?(): void;

  onInit?(): void;
}

export interface CardConfig {
  mwcSize?: MwcSizeDSType;
  mwcHoverable?: boolean;
  mwcBordered?: boolean;
  mwcBorderless?: boolean;
}

export interface CarouselConfig {
  mwcAutoPlay?: boolean;
  mwcAutoPlaySpeed?: boolean;
  mwcDots?: boolean;
  mwcEffect?: 'scrollx' | 'fade' | string;
  mwcEnableSwipe?: boolean;
  mwcVertical?: boolean;
  mwcLoop?: boolean;
}

export interface CascaderConfig {
  mwcSize?: string;
  mwcBackdrop?: boolean;
}

export interface CollapseConfig {
  mwcAccordion?: boolean;
  mwcBordered?: boolean;
  mwcGhost?: boolean;
}

export interface CollapsePanelConfig {
  mwcShowArrow?: boolean;
}

export interface DatePickerConfig {
  mwcSeparator?: string;
  mwcSuffixIcon?: string | TemplateRef<MwcSafeAny>;
  mwcBackdrop?: boolean;
  mwcCloseOnSelect?: boolean;
}

export interface DescriptionsConfig {
  mwcBordered?: boolean;
  mwcColumn?: { [key in MwcBreakpointEnum]?: number } | number;
  mwcSize?: 'default' | 'middle' | 'small';
  mwcColon?: boolean;
}

export interface DrawerConfig {
  mwcMask?: boolean;
  mwcMaskClosable?: boolean;
  mwcCloseOnNavigation?: boolean;
  mwcDirection?: Direction;
}

export interface DropDownConfig {
  mwcBackdrop?: boolean;
}

export interface EmptyConfig {
  mwcDefaultEmptyContent?: Type<MwcSafeAny> | TemplateRef<string> | string | undefined;
}

export interface FilterTriggerConfig {
  mwcBackdrop?: boolean;
}

export interface FormConfig {
  mwcNoColon?: boolean;
  mwcAutoTips?: Record<string, Record<string, string>>;
  mwcTooltipIcon?: string | { type: string; theme: ThemeType };
}

export interface IconConfig {
  mwcTheme?: 'fill' | 'outline' | 'twotone';
  mwcTwotoneColor?: string;
}

export interface MessageConfig {
  mwcAnimate?: boolean;
  mwcDuration?: number;
  mwcMaxStack?: number;
  mwcPauseOnHover?: boolean;
  mwcTop?: number | string;
  mwcDirection?: Direction;
}

export interface ModalConfig {
  mwcMask?: boolean;
  mwcMaskClosable?: boolean;
  mwcCloseOnNavigation?: boolean;
  mwcDirection?: Direction;
}

export interface NotificationConfig extends MessageConfig {
  mwcTop?: string | number;
  mwcBottom?: string | number;
  mwcPlacement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'bottom';
}

export interface PageHeaderConfig {
  mwcGhost: boolean;
}

export interface PaginationConfig {
  mwcSize?: 'default' | 'small';
  mwcPageSizeOptions?: number[];
  mwcShowSizeChanger?: boolean;
  mwcShowQuickJumper?: boolean;
  mwcSimple?: boolean;
}

export interface ProgressConfig {
  mwcGapDegree?: number;
  mwcGapPosition?: 'top' | 'right' | 'bottom' | 'left';
  mwcShowInfo?: boolean;
  mwcStrokeSwitch?: number;
  mwcStrokeWidth?: number;
  mwcSize?: 'default' | 'small';
  mwcStrokeLinecap?: 'round' | 'square';
  mwcStrokeColor?: string;
}

export interface RateConfig {
  mwcAllowClear?: boolean;
  mwcAllowHalf?: boolean;
}

export interface SegmentedConfig {
  mwcSize?: MwcSizeLDSType;
}

export interface SpaceConfig {
  mwcSize?: 'small' | 'middle' | 'large' | number;
}

export interface SpinConfig {
  mwcIndicator?: TemplateRef<MwcSafeAny>;
}

export interface SwitchConfig {
  mwcSize: MwcSizeDSType;
}

export interface TableConfig {
  mwcBordered?: boolean;
  mwcSize?: MwcSizeMDSType;
  mwcShowQuickJumper?: boolean;
  mwcLoadingIndicator?: TemplateRef<MwcSafeAny>;
  mwcShowSizeChanger?: boolean;
  mwcSimple?: boolean;
  mwcHideOnSinglePage?: boolean;
}

export interface TabsConfig {
  mwcAnimated?:
    | boolean
    | {
        inkBar: boolean;
        tabPane: boolean;
      };
  mwcSize?: MwcSizeLDSType;
  mwcType?: 'line' | 'card';
  mwcTabBarGutter?: number;
  mwcShowPagination?: boolean;
}

export interface TimePickerConfig {
  mwcAllowEmpty?: boolean;
  mwcClearText?: string;
  mwcNowText?: string;
  mwcOkText?: string;
  mwcFormat?: string;
  mwcHourStep?: number;
  mwcMinuteStep?: number;
  mwcSecondStep?: number;
  mwcPopupClassName?: string;
  mwcUse12Hours?: string;
  mwcSuffixIcon?: string | TemplateRef<MwcSafeAny>;
  mwcBackdrop?: boolean;
}

export interface TreeConfig {
  mwcBlockNode?: boolean;
  mwcShowIcon?: boolean;
  mwcHideUnMatched?: boolean;
}

export interface TreeSelectConfig {
  mwcShowIcon?: string;
  mwcShowLine?: boolean;
  mwcDropdownMatchSelectWidth?: boolean;
  mwcHideUnMatched?: boolean;
  mwcSize?: 'large' | 'small' | 'default';
  mwcBackdrop?: boolean;
}

export interface TypographyConfig {
  mwcEllipsisRows?: number;
  mwcCopyTooltips?: [MwcTSType, MwcTSType] | null;
  mwcCopyIcons: [MwcTSType, MwcTSType];
  mwcEditTooltip?: null | MwcTSType;
  mwcEditIcon: MwcTSType;
}

export interface ImageConfig {
  mwcFallback?: string;
  mwcPlaceholder?: string;
  mwcDisablePreview?: string;
  mwcCloseOnNavigation?: boolean;
  mwcDirection?: Direction;
}

export interface ImageExperimentalConfig {
  mwcFallback?: string;
  mwcPlaceholder?: string;
  mwcDisablePreview?: string;
  mwcCloseOnNavigation?: boolean;
  mwcDirection?: Direction;
  mwcAutoSrcset?: boolean;
  mwcSrcLoader?(params: { src: string; width: number }): string;
}

export interface PopConfirmConfig {
  mwcPopconfirmBackdrop?: boolean;
  mwcAutofocus?: null | 'ok' | 'cancel';
}

export interface PopoverConfig {
  mwcPopoverBackdrop?: boolean;
}

export type MwcConfigKey = keyof MwcConfig;

/**
 * User should provide an object implements this interface to set global configurations.
 */
export const NZ_CONFIG = new InjectionToken<MwcConfig>('mwc-config');
